import * as React from "react";
import { ActionButton, Badge, Card } from "@jhool-io/fe-components";

import { useParams, useSearchParams } from "react-router-dom";
import {
    cn,
    formatDate,
    makeStringFirstLetterCapital,
} from "../../../../utils/helpers";
import { ViewEditorText } from "../../../TextEditor/ViewEditorText/ViewEditorText";
import { useFetchSessionNote } from "../../../../hooks/queries/note";
import { useAppSelector } from "../../../../hooks/useRedux";
import PlusIcon from "../../../../icons/Plus";
import {
    IAddendumDetails,
    NoteStatus,
} from "../../../../modules/notes/types/notes.types";

interface NoteContentAddendumProps {
    /** Addendum header */
    headerText: string;
    /** Array of addendum items to display */
    addendums?: IAddendumDetails[];
    /** Field id of note content item */
    fieldId?: string;
    /** Note content item details */
    content?: React.ReactNode;
    /** has note content to display? */
    hasContent?: boolean;
    headingAnnotation?: React.ReactNode;
}

export default function NoteContentItem({
    headerText,
    addendums,
    fieldId,
    content,
    hasContent = true,
    headingAnnotation,
}: NoteContentAddendumProps) {
    const [searchParams, setSearchParams] = useSearchParams();

    const params = useParams();
    const noteId = params.noteId as string;
    const clientId = params.clientId as string;

    const { practice } = useAppSelector((state) => state.userPractice);

    const { data } = useFetchSessionNote(
        clientId,
        noteId,
        Boolean(clientId) && Boolean(noteId)
    );

    const handleButtonClick = (id: string) => {
        searchParams.set("field_id", id);
        searchParams.set("modal", "add_addendum");
        setSearchParams(searchParams);
    };

    const allowedNoteStatuses = [
        NoteStatus.PENDING_SUPERVISOR_REVIEW,
        NoteStatus.CHANGES_REQUESTED,
        NoteStatus.PENDING_CODE_REVIEW,
    ];

    const canShowButton =
        (data?.data.provider.provider_id === practice?.provider_id ||
            Boolean(
                practice?.provider_id &&
                    practice?.provider_id ===
                        data?.data?.provider?.supervisor_id
            )) &&
        allowedNoteStatuses.includes(data?.data.note_status as NoteStatus);
    const addendumCount = addendums?.length ?? 0;

    return (
        <Card>
            <div
                className={cn(
                    "pb-8 mb-2 [border-bottom:1px_dotted_var(--color-stroke)] group",
                    {
                        "cursor-default": canShowButton,
                    }
                )}
            >
                <div className="flex items-center gap-x-[--spacing-items]">
                    <h3 className="text-textSec text-caption font-medium uppercase">
                        {headerText}
                    </h3>
                    {addendumCount > 0 ? (
                        <Badge
                            type="custom"
                            bgColor="color-mix(in srgb, var(--color-info-bg) 40%, transparent)"
                            textColor="var(--color-info-text)"
                            className="!text-small font-semibold normal-case"
                            content={`${addendumCount} addendum${
                                addendumCount === 1 ? "" : "s"
                            }`}
                        />
                    ) : null}
                    {canShowButton ? (
                        <ActionButton
                            variant="normal"
                            size="auto"
                            onClick={
                                fieldId
                                    ? () => handleButtonClick(fieldId)
                                    : undefined
                            }
                            className="group-hover:opacity-100 flex size-5 items-center justify-center rounded-full bg-[--card-bg-dark] shrink-0 opacity-0"
                            aria-label="add addendum"
                            actionTitle="Add addendum"
                        >
                            <PlusIcon />
                        </ActionButton>
                    ) : null}
                </div>
                {headingAnnotation}
            </div>
            {hasContent ? (
                <div className="text-text text-caption font-medium">
                    {content}
                </div>
            ) : null}
            {addendumCount > 0 ? (
                <section className="mt-4 flex flex-col gap-y-3 rounded-r-lg bg-[color-mix(in_srgb,var(--color-info-bg)_40%,transparent)] p-3 [border-left:1px_solid_color-mix(in_srgb,var(--color-info-text)_35%,transparent)]">
                    {addendums?.map((addendum) => (
                        <article
                            key={addendum.note_addendum_id}
                            className="rounded-md bg-[--card-bg] p-3"
                            id={addendum.field_id ?? undefined}
                            style={{ scrollMarginTop: "60px" }}
                        >
                            <h4 className="text-textSec font-medium text-caption flex items-center gap-x-1 mb-2">
                                <span>
                                    {`${makeStringFirstLetterCapital(
                                        addendum.author.first_name
                                    )} ${makeStringFirstLetterCapital(
                                        addendum.author.last_name
                                    )} added addendum`}
                                </span>
                                <span className="text-text">{`'${addendum.title}'`}</span>
                                <span>
                                    {`- ${formatDate(
                                        addendum.signature_date_time,
                                        false,
                                        "MM/dd/yyyy, h:mmaa"
                                    )}`}
                                </span>
                            </h4>
                            <div className="*:!text-caption *:!mb-0">
                                <ViewEditorText text={addendum.content} />
                            </div>
                        </article>
                    ))}
                </section>
            ) : null}
        </Card>
    );
}
